// src/pages/offlineSales/components/OrderReceiptPrintModal.jsx
import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { exportOfflineOrderPDF } from '../../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../../utils/themeColors';
import { getImageUrl } from '../../../utils/imageUtils';
import { buildThermalReceiptCss, printThermalReceipt, shortCashierName } from '../../../utils/thermalReceiptPrint';
import { getSettings } from '../../../services/api/offlineSales/settings';
import PrintPreviewModal from '../../../components/common/PrintPreviewModal/PrintPreviewModal';
import OfflineOrderA4Invoice from './OfflineOrderA4Invoice';
import styles from './OrderReceiptPrintModal.module.css';

const OrderReceiptPrintModal = ({ order, onClose }) => {
  const { t, formatCurrency } = useLocalization();
  const [settings, setSettings] = useState(null);

  // Receipt header / footer text lives in the offline sales settings (same as the terminal)
  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);

  if (!order) return null;

  const renderReceipt = () => (
    <div className={styles.receiptArea} id="order-receipt-80mm">
      {order.Shop?.logo && (
        <div className={styles.receiptLogoWrap}>
          <img
            src={new URL(getImageUrl(order.Shop.logo), window.location.href).href}
            alt={order.Shop.name || ''}
            className={styles.receiptLogo}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      )}
      {order.Shop?.name && <p className={styles.receiptShop}>{order.Shop.name}</p>}

      {settings?.receiptHeader && (
        <div className={styles.receiptHeader}>{settings.receiptHeader}</div>
      )}

      <div className={styles.receiptMeta}>
        <div className={styles.receiptMetaRow}>
          <span>{t('offlineSales.orderNumber')}</span>{' '}
          <span>{order.orderNumber}</span>
        </div>
        <div className={styles.receiptMetaRow}>
          <span>{t('offlineSales.date')}</span>{' '}
          <span>{new Date(order.createdAt).toLocaleString()}</span>
        </div>
        <div className={styles.receiptMetaRow}>
          <span>{t('offlineSales.cashier')}</span>{' '}
          <span>{shortCashierName(order.cashier?.name) || '—'}</span>
        </div>
        <div className={styles.receiptMetaRow}>
          <span>{t('offlineSales.customer')}</span>{' '}
          <span>{order.customer?.name || t('offlineSales.cashSales')}</span>
        </div>
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptItems}>
        {(order.orderItems || []).map((item) => (
          <div key={item.id} className={styles.receiptItem}>
            <span className={styles.receiptItemName}>
              {item.Product?.name}
              {item.ProductVariation?.sku ? ` — ${item.ProductVariation.sku}` : ''}
              {item.freeQuantity > 0 && ` (${t('offlineSales.itemsIncludedFree', { count: item.freeQuantity })})`}
            </span>
            <span className={styles.receiptItemQty}>×{item.quantity}</span>
            <span className={styles.receiptItemTotal}>{formatCurrency(item.lineTotal)}</span>
          </div>
        ))}
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptTotals}>
        {parseFloat(order.totalDiscount) > 0 && (
          <div className={styles.receiptTotalRow}>
            <span>{t('offlineSales.itemDiscount')}</span>
            <span>-{formatCurrency(order.totalDiscount)}</span>
          </div>
        )}
        {parseFloat(order.orderDiscount) > 0 && (
          <div className={styles.receiptTotalRow}>
            <span>{t('offlineSales.orderDiscount')}</span>
            <span>-{formatCurrency(order.orderDiscount)}</span>
          </div>
        )}
        {parseFloat(order.loyaltyDiscountAmount) > 0 && (
          <div className={styles.receiptTotalRow}>
            <span>{t('offlineSales.loyaltyDiscount')}</span>
            <span>-{formatCurrency(order.loyaltyDiscountAmount)}</span>
          </div>
        )}
        {parseFloat(order.deliveryCharge) > 0 && (
          <div className={styles.receiptTotalRow}>
            <span>{t('offlineSales.deliveryCharge')}</span>
            <span>+{formatCurrency(order.deliveryCharge)}</span>
          </div>
        )}
        <div className={`${styles.receiptTotalRow} ${styles.receiptGrandTotal}`}>
          <strong>{t('offlineSales.grandTotal')}</strong>
          <strong>{formatCurrency(order.grandTotal)}</strong>
        </div>
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptPayments}>
        <div className={styles.receiptPaymentHeader}>{t('offlineSales.payments')}</div>
        {(order.paymentRecords || []).map((p) => (
          <div key={p.id} className={styles.receiptTotalRow}>
            <span>{t(`offlineSales.${p.PaymentMethod?.key}`, p.PaymentMethod?.label || p.PaymentMethod?.key)}</span>
            <span>{formatCurrency(p.paymentAmount)}</span>
          </div>
        ))}
        <div className={styles.receiptTotalRow}>
          <span>{t('offlineSales.totalPaid')}</span>
          <strong>{formatCurrency(order.paidAmount)}</strong>
        </div>
        {parseFloat(order.changeAmount) > 0 && (
          <div className={styles.receiptTotalRow}>
            <span>{t('offlineSales.change')}</span>
            <strong>{formatCurrency(order.changeAmount)}</strong>
          </div>
        )}
        {parseFloat(order.balanceAmount) > 0 && (
          <div className={styles.receiptTotalRow}>
            <span>{t('offlineSales.balanceDue')}</span>
            <strong style={{ color: 'var(--color-error)' }}>{formatCurrency(order.balanceAmount)}</strong>
          </div>
        )}
      </div>

      {parseFloat(order.loyaltyPointsEarned) > 0 && (
        <div className={styles.receiptLoyalty}>
          +{order.loyaltyPointsEarned} {t('offlineSales.loyaltyPointsLabel')} {t('common.earned', 'earned')}
        </div>
      )}

      {settings?.receiptFooter && (
        <div className={styles.receiptFooter}>{settings.receiptFooter}</div>
      )}
    </div>
  );

  // Receipt mode → same isolated 80mm thermal print as the terminal; A4 → normal browser print
  const handlePrint = (mode) => {
    if (mode === 'a4') {
      window.print();
      return;
    }
    printThermalReceipt(document.getElementById('order-receipt-80mm'), {
      title: `Receipt ${order.orderNumber || ''}`,
      css: buildThermalReceiptCss(styles),
    });
  };

  return (
    <PrintPreviewModal
      title={`${t('offlineSales.printOrderTitle', 'Print Order')} — #${order.orderNumber}`}
      onClose={onClose}
      pdfFileName={`Offline_Order_${order.orderNumber || "Invoice"}.pdf`}
      onDownloadPdf={() => exportOfflineOrderPDF(order, {
        primary: getCssVar('--color-primary', '#4f46e5'),
        primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
      })}
      onPrint={handlePrint}
      renderReceipt={renderReceipt}
      renderA4={() => <OfflineOrderA4Invoice order={order} />}
    />
  );
};

export default OrderReceiptPrintModal;
